import { parsePhoneNumber } from 'react-phone-number-input'

/**
 * Connect rejects malformed dial endpoints with a `BadEndpointException` whose
 * message carries the entire Streams stack trace. Surfacing that raw payload in
 * a toast is unreadable, so validate before dialing and map anything that still
 * fails onto a short, human message.
 */

export type DialGuardResult =
    | { ok: true; e164: string }
    | { ok: false; message: string }

const countryName = (iso2: string): string => {
    try {
        return new Intl.DisplayNames(undefined, { type: 'region' }).of(iso2.toUpperCase()) || iso2.toUpperCase()
    } catch {
        return iso2.toUpperCase()
    }
}

/**
 * Validates raw phone input as E.164 and checks the country against the agent's
 * dialable list. `dialableCountries` holds lowercase iso2 codes (see
 * streamsStore.onAgentInit); an empty list means "no restriction configured".
 */
export function validateDialNumber(raw: string, dialableCountries: string[] = []): DialGuardResult {
    const value = (raw || '').trim()
    if (!value) return { ok: false, message: 'Enter a phone number.' }
    if (!value.startsWith('+')) {
        return { ok: false, message: 'Include the country code, for example +1 555 123 4567.' }
    }

    let parsed: ReturnType<typeof parsePhoneNumber>
    try {
        parsed = parsePhoneNumber(value)
    } catch {
        parsed = undefined
    }
    if (!parsed || !parsed.isValid()) {
        return { ok: false, message: 'That number doesn’t look valid. Check the digits and country code.' }
    }

    const iso2 = (parsed.country || '').toLowerCase()
    if (dialableCountries.length > 0 && iso2 && !dialableCountries.includes(iso2)) {
        return { ok: false, message: `Your account isn’t allowed to dial ${countryName(iso2)} numbers.` }
    }

    return { ok: true, e164: parsed.number }
}

const errorText = (err: unknown): string => {
    if (err == null) return ''
    if (typeof err === 'string') return err
    if (err instanceof Error) return err.message
    if (typeof err === 'object') {
        try {
            return JSON.stringify(err)
        } catch {
            return String(err)
        }
    }
    return String(err)
}

const DIAL_ERROR_PATTERNS: Array<[RegExp, string]> = [
    [/BadEndpointException|phone number is invalid|invalid phone/i, 'That number was rejected by the phone network. Check the number and country code.'],
    [/cannot be transferred|canTransferContact/i, 'This contact cannot have participants added right now.'],
    [/already in progress/i, 'Still adding the previous participant — please wait a moment.'],
    [/timed out/i, 'The call took too long to connect. Please try again.'],
    [/UnauthorizedException|AccessDenied|not allowed/i, 'You are not permitted to dial this number.'],
    [/LimitExceeded|too many/i, 'This call already has the maximum number of participants.'],
    [/CTIException|Destination not connected|NetworkError|Failed to fetch/i, 'Couldn’t reach the phone network. Please try again.'],
]

/** Maps a Streams/CTI dial failure onto a short message safe to show in a toast. */
export function friendlyDialError(err: unknown, fallback = 'Couldn’t add the participant. Please try again.'): string {
    const text = errorText(err)
    if (!text) return fallback
    const match = DIAL_ERROR_PATTERNS.find(([pattern]) => pattern.test(text))
    return match ? match[1] : fallback
}
